import React, {useEffect, useState, useContext} from 'react';
import {
  Platform,
  View,
  Keyboard,
  InputAccessoryView,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import Header from '../../components/elements/Header';
import Divider from '../../components/elements/Divider';
import MobilePhoneInput from '../../components/elements/MobilePhoneInput';
import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {fetchApi} from '../../utils/ApiHelper';
import {
  Pages,
  SEND_OTP_TYPES,
  BUTTON_TYPES,
} from '../../utils/Constants';
import {
  getCountryCode,
  transformPhoneForAPI,
  trimSpace,
  validateEmail,
} from '../../utils/HelperMethods';
import Text from '../../components/elements/Text';
import Button from '../../components/elements/Button';
import {HTTP_METHODS, SEND_OTP, VERIFY_EMAIL} from '../../utils/URLConstants';
import BreadCrumbs from '../../components/elements/BreadCrumbs';
import Input from '../../components/elements/Input';
import * as Keychain from 'react-native-keychain';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import {tID} from '../../utils/TestIdHelper';
import CommonStyles from '../../utils/CommonStyles';
import VerifyCode from '../../components/modules/VerifyCode';

const EmailInputForm = ({
  inactiveEmailError,
  email,
  setEmail = () => {},
  emailError,
  verifyEmail = () => {},
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <View
      testID={tID('ForgotPasswordPhoneVerification-View-EmailInputForm')}
      style={styles.emailContainer}>
      <Input
        testID={tID('ForgotPasswordPhoneVerification-Input-Email')}
        inputTitle="Email Address"
        placeholder="Type Here..."
        errorText={inactiveEmailError || 'Please enter a valid Email Address.'}
        value={email}
        onChangeText={setEmail}
        topMargin={Platform.OS === 'web' ? 16 : 8}
        hasError={emailError || !!inactiveEmailError}
        style={Platform.OS === 'web' && styles.flex1Width292}
      />
      <Button
        testID={tID('ForgotPasswordPhoneVerification-Button-VerifyEmail')}
        disabled={email === '' || emailError || !!inactiveEmailError}
        onPress={verifyEmail}
        title="Verify Email"
        buttonType={BUTTON_TYPES.Filled}
        titleColor={colors.textOnAccent}
        style={Platform.OS === 'web' && styles.flex1Width292}
        topMargin={Platform.OS === 'web' ? 24 : 0}
      />
    </View>
  );
};

const MobileInputForm = ({
  labelText,
  phoneNo,
  setphoneNo,
  phoneNoHasError,
  showVerificationSection,
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <View
      testID={tID('ForgotPasswordPhoneVerification-View-MobileInputForm')}
      style={styles.root}>
      <View style={styles.mobileInputContainer}>
        <MobilePhoneInput
          inputTitle={labelText}
          phoneNumber={phoneNo}
          onChangePhoneNumber={setphoneNo}
          leftText={getCountryCode()}
          hasError={phoneNoHasError}
          inputAccessoryViewID={'mobileNumberInput'}
          showFlag={false}
        />
      </View>
      <View style={styles.buttonsContainer}>
        <Button
          testID={tID('ForgotPasswordPhoneVerification-Button-Reset')}
          disabled={phoneNo === '' ? true : false}
          onPress={showVerificationSection}
          title={'Send Password Reset Code'}
          buttonType={BUTTON_TYPES.Filled}
          titleColor={colors.textOnAccent}
          style={Platform.OS === 'web' && styles.flex1Width292}
        />
      </View>
      {Platform.OS == 'ios' && (
        <InputAccessoryView nativeID={'mobileNumberInput'}>
          <View style={styles.accessoryContainer}>
            <View />
            <Text
              testID={tID('ForgotPasswordPhoneVerification-Text-Done')}
              style={styles.doneButton}
              onPress={Keyboard.dismiss}>
              Done
            </Text>
          </View>
        </InputAccessoryView>
      )}
    </View>
  );
};

const ForgotPasswordPhoneVerification = ({navigation, ...props}) => {
  //forwarding web only props
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const [title, setTitle] = useState('Please provide your email address');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [inactiveEmailError, setInactiveEmailError] = useState(false);
  const [phoneNo, setphoneNo] = useState('');
  const [phoneNoHasError, setPhoneNoHasError] = useState(false);
  const [activPage, setActivepage] = useState(Pages.Phone);
  const [labelText, setLabelText] = useState('Phone Number');

  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);

  useEffect(() => {
    if (email && !validateEmail(email)) {
      setEmailError(true);
    } else {
      setEmailError(false);
      setInactiveEmailError(false);
    }
  }, [email]);

  const verifyEmail = () => {
    lpc.setVisible(true);
    // Dlog('verifying mail');
    const postData = {
      email: email.toLowerCase(),
    };
    fetchApi(HTTP_METHODS.POST, VERIFY_EMAIL, postData)
      .then(response => {
        lpc.setVisible(false);
        // Dlog(response.data);
        if (response.status == 200) {
          if (Platform.OS === 'web') {
            props.setAuthCredentials({
              //save in state and not in storage for web
              username: trimSpace(email).toLowerCase(),
            });
          } else {
            Keychain.setGenericPassword(
              trimSpace(email).toLowerCase(),
              'wrongPassword', //needed for android as it throws error if the string is empty
            );
          }
          setTitle('Enter your phone number');
        }
      })
      .catch(error => {
        lpc.setVisible(false);
        setEmailError(true); //for completeness sake
        // Dlog(error.data);
        if (error.data) {
          if (error.data.errors instanceof Array) {
            alert(error.data.errors[0].message);
          } else if (typeof error.data.errors === 'string') {
            alert(error.data.errors);
          }
        } else if (error) {
          setInactiveEmailError(error);
          // alert(error);
        }
      });
  };

  const showVerificationSection = () => {
    const postData = {
      email: email.toLowerCase(),
      phone: transformPhoneForAPI(phoneNo),
      type: SEND_OTP_TYPES.existing,
    };
    lpc.setVisible(true);
    fetchApi(HTTP_METHODS.POST, SEND_OTP, postData)
      .then(response => {
        // Dlog(response);
        lpc.setVisible(false);
        if (response.status == 200) {
          setActivepage(Pages.Verify);
          setTitle('Phone Verification');

          setLabelText('Code');
        }
      })
      .catch(error => {
        // Dlog(error);
        lpc.setVisible(false);
        esc.showError(error);
        // if (error.data) {
        //   if (error.data.errors instanceof Array) {
        //     alert(error.data.errors[0].message);
        //   } else if (typeof error.data.errors === 'string') {
        //     alert(error.data.errors);
        //   }
        // }
      });
  };

  return (
    <View
      testID={tID('ForgotPasswordPhoneVerification-View-Main')}
      style={styles.maincontainer}>
      <SafeAreaView>
        <View
          testID={tID('ForgotPasswordPhoneVerification-View-HeaderContainer')}
          style={styles.headerView}>
          {Platform.OS != 'web' && (
            <Header
              testID={tID('ForgotPasswordPhoneVerification-Header-Verify')}
              title="Password Recovery"
              onPressLeftIcon={navigation.goBack}
            />
          )}
        </View>
        <View style={styles.container}>
          <Text
            testID={tID('ForgotPasswordPhoneVerification-Text-Title')}
            style={styles.title}
            color={colors.textPrimary}>
            {Platform.OS === 'web' ? 'Forgot Password' : title}
          </Text>
          {title === 'Please provide your email address' ? (
            <Text
              testID={tID('ForgotPasswordPhoneVerification-Text-SubTitle1')}
              variation="Body1Regular"
              color={colors.textSecondary}
              style={styles.subtitle}>
              By providing your registered email address, we can verify your
              account.
            </Text>
          ) : title === 'Enter your phone number' ? (
            <Text
              testID={tID('ForgotPasswordPhoneVerification-Text-SubTitle2')}
              variation="Body1Regular"
              color={colors.textSecondary}
              style={styles.subtitle}>
              Please provide your registered phone number to receive an SMS with
              your recovery code
            </Text>
          ) : (
            <Text
              testID={tID('ForgotPasswordPhoneVerification-Text-SubTitle3')}
              variation="Body1Regular"
              color={colors.textSecondary}
              style={styles.subtitle}>
              Please enter the 6-digit code sent to you at
              <Text
                testID={tID('ForgotPasswordPhoneVerification-Text-PhoneNumber')}
                variation="Body1SemiBold"
                color={colors.textPrimary}>
                {' ' + getCountryCode() + ' ' + transformPhoneForAPI(phoneNo)}
              </Text>
            </Text>
          )}
        </View>
        <View style={styles.container}>
          {title === 'Please provide your email address' ? (
            <BreadCrumbs current={1} total={3} />
          ) : (
            <BreadCrumbs current={activPage + 1} total={3} />
          )}
          {Platform.OS === 'web' && <Divider />}
        </View>
      </SafeAreaView>
      {title === 'Please provide your email address' ? (
        <EmailInputForm
          inactiveEmailError={inactiveEmailError}
          email={email}
          setEmail={setEmail}
          emailError={emailError}
          verifyEmail={verifyEmail}
        />
      ) : (
        <View style={CommonStyles.full_flex}>
          {activPage === Pages.Phone && (
            <MobileInputForm
              labelText={labelText}
              phoneNo={phoneNo}
              setphoneNo={setphoneNo}
              phoneNoHasError={phoneNoHasError}
              showVerificationSection={showVerificationSection}
            />
          )}
          {activPage === Pages.Verify && (
            <VerifyCode
              phoneNo={phoneNo}
              authCredentials={props?.authCredentials} //needed to access the email
              setAuthCredentials={props?.setAuthCredentials} //needed to save the password recorvery token
              onVerificationSuccess={() => props?.moveToNextStep(2)} //needed to move to password recovery screen
            />
          )}
        </View>
      )}
    </View>
  );
};

export default ForgotPasswordPhoneVerification;
